import { useId, type Ref } from 'react';
import {
  DISPLAY_NAME_MAX_LENGTH,
  DisplayName,
  USERNAME_MAX_LENGTH,
  Username,
  type AdminMember,
  type Role,
} from '@grc/shared';
import { Field } from '../../components/Field';
import { t, type Code } from '../../i18n';

/** What the add and the edit forms hold: the three things an admin sets (D23). */
export type MemberValues = { displayName: string; username: string; role: Role };

export function valuesOf(member: AdminMember): MemberValues {
  return { displayName: member.displayName, username: member.username, role: member.role };
}

/**
 * The values checked as the API checks them, before anything is sent (the account rules, D20):
 * the values as the API keeps them, an empty field (it takes the focus, as A3's forms do), or the
 * username's rule broken.
 */
export type Checked =
  | { kind: 'ok'; displayName: string; username: string }
  | { kind: 'empty'; field: 'displayName' | 'username' }
  | { kind: 'rule'; error: Code };

export function checkValues(values: MemberValues): Checked {
  // Only an empty name can fail: the input takes 60 characters at most.
  const displayName = DisplayName.safeParse(values.displayName);
  if (!displayName.success) return { kind: 'empty', field: 'displayName' };
  if (values.username.trim() === '') return { kind: 'empty', field: 'username' };
  const username = Username.safeParse(values.username);
  if (!username.success) return { kind: 'rule', error: 'error.usernameRule' };
  return { kind: 'ok', displayName: displayName.data, username: username.data };
}

const ROLES: readonly [Role, Code][] = [
  ['member', 'member.roleMember'],
  ['admin', 'member.roleAdmin'],
];

type MemberFieldsProps = {
  values: MemberValues;
  onChange: (values: MemberValues) => void;
  /** Whether the role is offered: not on the admin's own account (D23). */
  withRole: boolean;
  displayNameRef: Ref<HTMLInputElement>;
  usernameRef: Ref<HTMLInputElement>;
};

/** The display name, the username and the role: adding a member, and on their page. */
export function MemberFields({
  values,
  onChange,
  withRole,
  displayNameRef,
  usernameRef,
}: MemberFieldsProps) {
  const roleName = useId();
  return (
    <>
      <Field
        ref={displayNameRef}
        label={t('member.displayName')}
        hint={t('member.displayNameHint')}
        name="member-display-name"
        autoComplete="off"
        maxLength={DISPLAY_NAME_MAX_LENGTH}
        aria-required="true"
        value={values.displayName}
        onChange={(e) => onChange({ ...values, displayName: e.target.value })}
      />
      {/* Someone else's username: the phone's password manager has nothing to fill in here. */}
      <Field
        ref={usernameRef}
        label={t('member.username')}
        hint={t('member.usernameHint')}
        name="member-username"
        autoComplete="off"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        maxLength={USERNAME_MAX_LENGTH}
        aria-required="true"
        value={values.username}
        onChange={(e) => onChange({ ...values, username: e.target.value })}
      />
      {withRole && (
        <fieldset className="choice">
          <legend>{t('member.role')}</legend>
          {ROLES.map(([role, label]) => (
            <label key={role} className="choice-option">
              <input
                type="radio"
                name={roleName}
                value={role}
                checked={values.role === role}
                onChange={() => onChange({ ...values, role })}
              />
              {t(label)}
            </label>
          ))}
        </fieldset>
      )}
    </>
  );
}
